import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import AdsClickOutlinedIcon from "@mui/icons-material/AdsClickOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { apiClient, fetchData } from "../../api/client";
import { openCallPopupWindow } from "../../utils/openCallPopupWindow";
import IfCan from "../../components/auth/IfCan";
import {
  formatCount,
  formatRate,
  formatScheduleAt,
  formatOutboundChannelLabel,
  labelChannel,
  labelLeadStatus,
  labelStatus,
  statusColor,
  toFriendlyMessage,
} from "./campaignUi";

const BRL = (n) =>
  Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function cardShadow(theme) {
  return theme.palette.mode === "dark"
    ? `0 1px 4px ${alpha("#000", 0.35)}`
    : `0 1px 3px ${alpha(theme.palette.common.black, 0.08)}`;
}

function KpiCard({ icon, title, value, color, sub, onClick }) {
  return (
    <Card
      elevation={1}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.();
        }
      }}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: 1,
        borderColor: "divider",
        boxShadow: (th) => cardShadow(th),
        cursor: "pointer",
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        "&:hover": {
          borderColor: color,
        },
      }}
    >
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", py: 2.25 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
          <Avatar sx={{ bgcolor: color, width: 44, height: 44, color: "#fff" }}>{icon}</Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" noWrap>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ color, lineHeight: 1.15, fontWeight: 600 }}>
              {formatCount(value) || "0"}
            </Typography>
            {sub ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35, lineHeight: 1.4 }}>
                {sub}
              </Typography>
            ) : null}
            <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.6, fontWeight: 600 }}>
              Ver solicitações
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function InfoPanel({ icon, title, subtitle, children, sx }) {
  return (
    <Card
      elevation={1}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: 1,
        borderColor: "divider",
        boxShadow: (th) => cardShadow(th),
        ...sx,
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          p: { xs: 2.25, sm: 2.75 },
          "&:last-child": { pb: { xs: 2.25, sm: 2.75 } },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.5 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <Divider sx={{ mb: 1.75 }} />
        <Box sx={{ flex: 1 }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

function StatRow({ label, value, hint }) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body1" fontWeight={700}>
          {value}
        </Typography>
      </Stack>
      {hint ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.4 }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

const INTERNAL_FAILURES = {
  consent_denied: {
    title: "Sem consentimento",
    detail: "Este contato não autorizou receber mensagens, ou pediu para sair da lista.",
  },
  opted_out: {
    title: "Pediu para sair da lista",
    detail: "O contato optou por não receber mais mensagens neste canal.",
  },
  provider_window_closed: {
    title: "Fora da janela de mensagens",
    detail:
      "Este canal só envia se já existir uma conversa recente. Sem resposta do contato, o disparo não sai.",
  },
  desconhecido: {
    title: "Motivo não informado",
    detail: "O provedor não explicou a falha. Abra a solicitação para ver o que aconteceu com este contato.",
  },
};

const FAILURE_CODE_COPY = {
  130472: {
    title: "Número em teste do WhatsApp",
    detail:
      "Este telefone está em um teste do WhatsApp que limita mensagens de marketing. A Meta recusou o envio — não dá para forçar agora. Tente de novo mais tarde ou fale com a pessoa por outro canal.",
  },
  131026: {
    title: "Mensagem não entregue",
    detail:
      "O WhatsApp não conseguiu entregar a mensagem. O número pode estar sem WhatsApp, inativo, bloqueado ou em região restrita. Confira o telefone na solicitação antes de reenviar.",
  },
};

function extractFailureCode(reason) {
  const match = String(reason || "").match(/\(#(\d+)\)|#(\d+)/);
  if (!match) return null;
  const code = Number(match[1] || match[2]);
  return Number.isFinite(code) ? code : null;
}

function describeLocalFailure(reason) {
  const raw = String(reason || "").trim();
  if (INTERNAL_FAILURES[raw]) return INTERNAL_FAILURES[raw];
  if (/part of an experiment/i.test(raw)) return FAILURE_CODE_COPY[130472];
  const code = extractFailureCode(raw);
  if (code && FAILURE_CODE_COPY[code]) return FAILURE_CODE_COPY[code];
  return { title: raw || "Motivo não informado", detail: null };
}

function presentFailure(failure) {
  const local = describeLocalFailure(failure?.reason);
  const title = failure?.title || local.title;
  const detail = failure?.detail || local.detail;
  return {
    title,
    detail: detail && detail !== title ? detail : null,
    count: Number(failure?.count || 0),
    items: Array.isArray(failure?.items) ? failure.items : [],
    reason: failure?.reason || title,
  };
}

function failureContactMatches(contact, failure) {
  if (!contact || !failure) return false;
  if (contact.errorTitle && failure.title && contact.errorTitle === failure.title) return true;
  const code = extractFailureCode(failure.reason);
  if (code && Number(contact.errorCode) === code) return true;
  if (failure.reason && contact.reason && String(failure.reason).trim() === String(contact.reason).trim()) {
    return true;
  }
  return false;
}

function FailureReasonBlock({ failure, onOpenLead, loadingContacts }) {
  const shown = failure.items || [];
  const hidden = Math.max(0, failure.count - shown.length);
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.4 }}>
            {failure.title}
          </Typography>
          {failure.detail ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.4, lineHeight: 1.45 }}
            >
              {failure.detail}
            </Typography>
          ) : null}
        </Box>
        <Typography variant="body1" fontWeight={700} sx={{ flexShrink: 0 }}>
          {formatCount(failure.count)}
        </Typography>
      </Stack>
      {shown.length ? (
        <Stack spacing={0.75} sx={{ mt: 1.1 }}>
          {shown.map((item) => {
            const name = item.clientName || (item.leadId ? `Solicitação #${item.leadId}` : "Contato");
            const address = item.address && item.address !== name ? item.address : null;
            return (
              <Button
                key={item.leadId || item.recipientId || `${item.clientId || "c"}-${item.address || name}`}
                variant="outlined"
                size="small"
                disabled={!item.leadId}
                onClick={() => onOpenLead(item.leadId)}
                sx={{
                  justifyContent: "space-between",
                  textTransform: "none",
                  textAlign: "left",
                  px: 1.25,
                  py: 0.75,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {name}
                  </Typography>
                  {address ? (
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {address}
                    </Typography>
                  ) : null}
                </Box>
                <Typography variant="caption" sx={{ flexShrink: 0, ml: 1, fontWeight: 700 }}>
                  {item.leadId ? "Abrir solicitação" : "Sem solicitação"}
                </Typography>
              </Button>
            );
          })}
          {hidden > 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              E mais {formatCount(hidden)} com o mesmo motivo. A lista completa está em Exportar lista.
            </Typography>
          ) : null}
        </Stack>
      ) : loadingContacts ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Buscando a solicitação…
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, lineHeight: 1.4 }}>
          Não encontrei a solicitação ligada a este erro.
        </Typography>
      )}
    </Box>
  );
}

export default function WhatsappCampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState("");
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [budgetSavedMsg, setBudgetSavedMsg] = useState("");
  const [bucketDialog, setBucketDialog] = useState(null);
  const [bucketItems, setBucketItems] = useState([]);
  const [bucketTotal, setBucketTotal] = useState(0);
  const [bucketLoading, setBucketLoading] = useState(false);
  const [bucketError, setBucketError] = useState("");
  const [failureContacts, setFailureContacts] = useState([]);
  const [failureContactsLoading, setFailureContactsLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetchData(`/campaigns/broadcasts/${id}`, "GET");
      const next = res?.data || null;
      setData(next);
      const currentBudget =
        next?.budget != null
          ? next.budget
          : next?.config_json?.budget != null
            ? next.config_json.budget
            : "";
      setBudgetDraft(
        currentBudget === "" || currentBudget == null ? "" : String(currentBudget)
      );
    } catch (err) {
      setErrorMsg(toFriendlyMessage(err));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const reasons = data?.failureReasons || [];
    if (!id || !reasons.length) {
      setFailureContacts([]);
      return undefined;
    }
    if (reasons.some((row) => Array.isArray(row.items) && row.items.length)) {
      setFailureContacts([]);
      setFailureContactsLoading(false);
      return undefined;
    }
    let cancel = false;
    setFailureContactsLoading(true);
    (async () => {
      try {
        const [failedRes, suppressedRes] = await Promise.all([
          fetchData(`/campaigns/broadcasts/${id}/contacts?bucket=failed`, "GET"),
          fetchData(`/campaigns/broadcasts/${id}/contacts?bucket=suppressed`, "GET"),
        ]);
        if (cancel) return;
        setFailureContacts([
          ...(failedRes?.data?.items || []),
          ...(suppressedRes?.data?.items || []),
        ]);
      } catch {
        if (!cancel) setFailureContacts([]);
      } finally {
        if (!cancel) setFailureContactsLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id, data]);

  const openBucket = async (kpi) => {
    setBucketDialog({ key: kpi.key, title: kpi.title });
    setBucketItems([]);
    setBucketTotal(Number(kpi.value) || 0);
    setBucketError("");
    setBucketLoading(true);
    try {
      const res = await fetchData(
        `/campaigns/broadcasts/${id}/contacts?bucket=${encodeURIComponent(kpi.bucket || kpi.key)}`,
        "GET"
      );
      const payload = res?.data || {};
      setBucketItems(Array.isArray(payload.items) ? payload.items : []);
      setBucketTotal(Number(payload.total ?? kpi.value) || 0);
    } catch (err) {
      setBucketError(toFriendlyMessage(err));
    } finally {
      setBucketLoading(false);
    }
  };

  const openRequestPopup = (leadId) => {
    openCallPopupWindow(leadId, { navigate });
  };

  const runAction = async (path) => {
    setBusy(true);
    setErrorMsg("");
    try {
      await fetchData(`/campaigns/broadcasts/${id}/${path}`, "POST", {});
      await refresh();
    } catch (err) {
      setErrorMsg(toFriendlyMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    setBusy(true);
    setErrorMsg("");
    try {
      const response = await apiClient.get(`/campaigns/broadcasts/${id}/export.csv`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `disparo-${id}-destinatarios.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMsg(toFriendlyMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const saveBudget = async () => {
    setBudgetSaving(true);
    setErrorMsg("");
    setBudgetSavedMsg("");
    try {
      const budget = budgetDraft === "" ? 0 : Number(budgetDraft);
      if (!Number.isFinite(budget) || budget < 0) {
        throw new Error("Informe um orçamento válido (R$ ≥ 0).");
      }
      const res = await fetchData(`/campaigns/broadcasts/${id}`, "PATCH", { budget });
      const next = res?.data || null;
      if (next) {
        setData((prev) => ({ ...(prev || {}), ...next }));
        setBudgetDraft(next.budget != null ? String(next.budget) : String(budget));
      }
      setBudgetSavedMsg("Orçamento salvo. O Dashboard → Campanhas usa este valor para custo/conversa e custo/venda.");
    } catch (err) {
      setErrorMsg(toFriendlyMessage(err));
    } finally {
      setBudgetSaving(false);
    }
  };

  const currentBudgetValue =
    data?.budget != null
      ? Number(data.budget)
      : data?.config_json?.budget != null
        ? Number(data.config_json.budget)
        : 0;
  const budgetDirty =
    (budgetDraft === "" ? 0 : Number(budgetDraft)) !== (Number.isFinite(currentBudgetValue) ? currentBudgetValue : 0);

  const failures = data?.failureReasons || [];
  const channels = data?.channels || [];
  const isEmail = channels.includes("email");
  const status = data?.status;
  const isDraftLike = ["draft", "scheduled", "paused"].includes(status);
  const neverLaunched =
    Number(data?.recipients || 0) === 0 &&
    Number(data?.sent || 0) === 0 &&
    status === "draft";

  const palette = useMemo(
    () => ({
      queue: theme.palette.info.main,
      sent: theme.palette.primary.main,
      delivered: theme.palette.success.main,
      read: theme.palette.secondary?.main ?? theme.palette.info.dark,
      replied: theme.palette.warning.main,
      failed: theme.palette.error.main,
      suppressed: theme.palette.text.secondary,
      click: theme.palette.info.dark,
      bounce: theme.palette.error.light,
    }),
    [theme]
  );

  const kpis = useMemo(() => {
    const base = [
      {
        key: "queue",
        title: "Na fila",
        value: data?.pending,
        color: palette.queue,
        icon: <HourglassEmptyOutlinedIcon />,
        sub: "Ainda aguardando envio",
      },
      {
        key: "sent",
        title: "Enviados",
        value: data?.sent,
        color: palette.sent,
        icon: <SendOutlinedIcon />,
        sub: "Aceitos pelo canal de envio",
      },
      {
        key: "delivered",
        title: "Entregues",
        value: data?.delivered,
        color: palette.delivered,
        icon: <DoneAllOutlinedIcon />,
        sub: isEmail
          ? "Aceitos/confirmados pelo provedor de e-mail"
          : "Chegaram ao celular ou caixa de entrada",
      },
      {
        key: "read",
        bucket: isEmail ? "opened" : "read",
        title: isEmail ? "Abertos" : "Lidos",
        value: isEmail ? data?.opened : data?.read,
        color: palette.read,
        icon: isEmail ? <MarkEmailReadOutlinedIcon /> : <VisibilityOutlinedIcon />,
        sub: isEmail ? "E-mails abertos pelos destinatários" : "Mensagens visualizadas",
      },
      {
        key: "replied",
        title: "Respostas",
        value: data?.replied,
        color: palette.replied,
        icon: <ReplyOutlinedIcon />,
        sub: "Pessoas que responderam depois do envio",
      },
    ];
    if (isEmail) {
      base.push(
        {
          key: "clicked",
          title: "Cliques",
          value: data?.clicked,
          color: palette.click,
          icon: <AdsClickOutlinedIcon />,
          sub: "Cliques em links do e-mail",
        },
        {
          key: "bounced",
          title: "Devolvidos",
          value: data?.bounced,
          color: palette.bounce,
          icon: <UndoOutlinedIcon />,
          sub: "Endereços inválidos ou rejeitados",
        }
      );
    }
    base.push(
      {
        key: "failed",
        title: "Falhas",
        value: data?.failed,
        color: palette.failed,
        icon: <ErrorOutlineOutlinedIcon />,
        sub: "Envios que não concluíram",
      },
      {
        key: "suppressed",
        title: "Não enviados",
        value: data?.suppressed,
        color: palette.suppressed,
        icon: <BlockOutlinedIcon />,
        sub: "Bloqueados por opt-out ou regra do canal",
      }
    );
    return base;
  }, [data, isEmail, palette]);

  const launchLabel =
    status === "paused" ? "Retomar envio" : status === "scheduled" ? "Iniciar agenda" : "Enviar agora";

  const pendingHint =
    status === "running"
      ? "Ainda serão processados neste ciclo"
      : neverLaunched
        ? "Aparece depois que o envio começar"
        : "Aguardando processamento ou próximo ciclo";

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 }, maxWidth: 1400, mx: "auto" }}>
      {errorMsg ? (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      ) : null}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={2.5}
        mb={3}
      >
        <Box sx={{ minWidth: 0 }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/campaigns")}
            sx={{ mb: 1 }}
          >
            Voltar aos envios
          </Button>
          {loading && !data ? (
            <Skeleton width={280} height={40} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {data?.name || "Envio em massa"}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55, maxWidth: 520 }}>
            Acompanhe quantas mensagens saíram, chegaram e geraram resposta neste disparo.
          </Typography>
          <Stack direction="row" spacing={1} mt={1.25} flexWrap="wrap" useFlexGap>
            {status ? (
              <Chip
                size="small"
                label={labelStatus(status)}
                color={statusColor(status)}
                variant={status === "running" ? "filled" : "outlined"}
              />
            ) : null}
            {data?.originName || data?.origin_name || data?.originIdentity || data?.origin_identity ? (
              <Chip
                size="small"
                label={`Canal de saída: ${formatOutboundChannelLabel({
                  channels,
                  originName: data.originName || data.origin_name,
                  originIdentity: data.originIdentity || data.origin_identity,
                })}`}
                variant="outlined"
                color="primary"
              />
            ) : (
              channels.map((ch) => (
                <Chip key={ch} size="small" label={labelChannel(ch) || ch} variant="outlined" />
              ))
            )}
            {data?.tag ? (
              <Chip size="small" label={`Etiqueta: ${data.tag}`} variant="outlined" />
            ) : null}
            {currentBudgetValue > 0 ? (
              <Chip
                size="small"
                label={`Orçamento ${BRL(currentBudgetValue)}`}
                variant="outlined"
              />
            ) : null}
          </Stack>
          {data?.schedule_at ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Agendado para {formatScheduleAt(data.schedule_at)}
            </Typography>
          ) : null}
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="text" onClick={refresh} disabled={loading || busy}>
            Atualizar
          </Button>
          <IfCan resource="campaigns" action="export">
            <Button variant="outlined" onClick={exportCsv} disabled={busy || loading || neverLaunched}>
              Exportar lista
            </Button>
          </IfCan>
          {isDraftLike ? (
            <IfCan resource="campaigns" action="send">
              <Button
                variant="contained"
                onClick={() => runAction("launch")}
                disabled={busy || loading}
              >
                {busy ? "Enviando…" : launchLabel}
              </Button>
            </IfCan>
          ) : null}
          {status === "running" ? (
            <IfCan resource="campaigns" action="update">
              <Button variant="outlined" onClick={() => runAction("pause")} disabled={busy}>
                Pausar envio
              </Button>
            </IfCan>
          ) : null}
          {["draft", "scheduled", "running", "paused"].includes(status) ? (
            <IfCan resource="campaigns" action="update">
              <Button
                variant="text"
                color="inherit"
                onClick={() => runAction("cancel")}
                disabled={busy}
              >
                Encerrar
              </Button>
            </IfCan>
          ) : null}
        </Stack>
      </Stack>

      {neverLaunched && !loading ? (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          Este envio ainda está em rascunho. Use <strong>Enviar agora</strong> para montar a
          audiência e iniciar — ou volte depois se ainda for ajustar a mensagem.
        </Alert>
      ) : null}

      {loading && !data ? (
        <Stack spacing={2.5}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
            }}
          >
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} variant="rounded" height={118} sx={{ borderRadius: 1 }} />
            ))}
          </Box>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={200} sx={{ borderRadius: 1 }} />
            ))}
          </Box>
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          {data?.originName ||
          data?.origin_name ||
          data?.originIdentity ||
          data?.origin_identity ||
          channels.length ? (
            <Card
              elevation={0}
              sx={{
                border: 1,
                borderColor: "divider",
                boxShadow: (th) => cardShadow(th),
              }}
            >
              <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Canal de saída
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600 }}>
                  {formatOutboundChannelLabel({
                    channels,
                    originName: data?.originName || data?.origin_name,
                    originIdentity: data?.originIdentity || data?.origin_identity,
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                  {channels.includes("voice")
                    ? "A ligação usa o número From deste Telefone Xpro. Falhas por ocupado/não atender seguem a política de retentativa do disparo (ou ficam como Não atendida)."
                    : "Remetente e credenciais deste disparo seguem a configuração do canal em Configurações → Canais."}
                </Typography>
              </CardContent>
            </Card>
          ) : null}

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              Resultados do envio
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.5 }}>
              Cada indicador mostra uma etapa do caminho da mensagem até a resposta.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              // Última linha incompleta cresce e preenche a largura (ex.: 4+3).
              "& > *": {
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 8px)",
                  md:
                    kpis.length % 3 === 0
                      ? "1 1 calc(33.333% - 11px)"
                      : "1 1 calc(25% - 12px)",
                },
                minWidth: 0,
              },
            }}
          >
            {kpis.map((kpi) => (
              <Box key={kpi.key} sx={{ display: "flex" }}>
                <KpiCard
                  icon={kpi.icon}
                  title={kpi.title}
                  value={kpi.value}
                  color={kpi.color}
                  sub={kpi.sub}
                  onClick={() => openBucket(kpi)}
                />
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              alignItems: "stretch",
            }}
          >
            {!neverLaunched ? (
              <InfoPanel
                icon={<VisibilityOutlinedIcon fontSize="small" />}
                title="Taxas do disparo"
                subtitle="Proporções sobre o que foi enviado e entregue."
              >
                <Box
                  sx={{
                    display: "grid",
                    gap: 1.75,
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  }}
                >
                  <StatRow
                    label="Taxa de entrega"
                    value={formatRate(data?.rates?.deliveryRate)}
                    hint={
                      isEmail
                        ? "Entregues ÷ enviados (aceite SES; Delivery detalhado quando houver evento)"
                        : "Entregues ÷ enviados"
                    }
                  />
                  <StatRow
                    label={isEmail ? "Taxa de abertura" : "Taxa de leitura"}
                    value={formatRate(
                      isEmail
                        ? data?.rates?.openRate ?? data?.rates?.readRate
                        : data?.rates?.readRate
                    )}
                    hint={isEmail ? "Abertos ÷ enviados" : "Lidos ÷ entregues"}
                  />
                  <StatRow
                    label="Taxa de resposta"
                    value={formatRate(data?.rates?.replyRate)}
                    hint="Respostas ÷ enviados"
                  />
                  <StatRow
                    label="Taxa de falha"
                    value={formatRate(data?.rates?.failureRate)}
                    hint="Falhas ÷ (enviados + falhas)"
                  />
                </Box>
              </InfoPanel>
            ) : null}

            <InfoPanel
              icon={<SpeedOutlinedIcon fontSize="small" />}
              title="Como está o envio"
              subtitle="Fila e ritmo em que as mensagens saem."
            >
              <Stack spacing={1.75}>
                <StatRow
                  label="Aguardando na fila"
                  value={formatCount(data?.pending) || "0"}
                  hint={pendingHint}
                />
                <StatRow
                  label="Ritmo máximo"
                  value={`${data?.throttle_per_minute ?? "—"} por minuto`}
                  hint="Limite para não sobrecarregar o canal"
                />
                {status === "running" ? (
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    O envio continua em segundo plano. Toque em <strong>Atualizar</strong> para ver
                    o progresso mais recente.
                  </Typography>
                ) : null}
              </Stack>
            </InfoPanel>

            <InfoPanel
              icon={<AttachMoneyOutlinedIcon fontSize="small" />}
              title="Orçamento do disparo"
              subtitle="Valor informado por você para custo por conversa/venda no dashboard. Pode editar a qualquer momento."
            >
              <IfCan
                resource="campaigns"
                action="update"
                fallback={
                  <StatRow
                    label="Orçamento atual"
                    value={currentBudgetValue > 0 ? BRL(currentBudgetValue) : "Não informado"}
                    hint="Sem permissão para editar. Peça a um admin com acesso a campanhas."
                  />
                }
              >
                <Stack spacing={1.5}>
                  <TextField
                    type="number"
                    label="Orçamento do disparo (R$)"
                    value={budgetDraft}
                    onChange={(e) => {
                      setBudgetDraft(e.target.value);
                      setBudgetSavedMsg("");
                    }}
                    inputProps={{ min: 0, step: "0.01" }}
                    fullWidth
                    size="small"
                    helperText="Usado no Dashboard → Campanhas para calcular custo por conversa e por venda. Edite também pela lista de envios (ícone de lápis)."
                  />
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={saveBudget}
                      disabled={budgetSaving || busy || loading || !budgetDirty}
                    >
                      {budgetSaving ? "Salvando…" : "Salvar orçamento"}
                    </Button>
                    {currentBudgetValue > 0 && !budgetDirty ? (
                      <Typography variant="caption" color="text.secondary">
                        Atual: {BRL(currentBudgetValue)}
                      </Typography>
                    ) : null}
                  </Stack>
                  {budgetSavedMsg ? (
                    <Alert severity="success" sx={{ py: 0.5 }}>
                      {budgetSavedMsg}
                    </Alert>
                  ) : null}
                </Stack>
              </IfCan>
            </InfoPanel>

            {!neverLaunched ? (
              <InfoPanel
                icon={<ReplyOutlinedIcon fontSize="small" />}
                title="Conversas geradas"
                subtitle={
                  (data?.leadsSample || []).length
                    ? `${formatCount((data.leadsSample || []).length)} conversa(s) ligadas a este disparo.`
                    : "Nenhuma conversa ligada a envios bem-sucedidos."
                }
              >
                {!(data?.leadsSample || []).length ? (
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                    Ainda não há conversas atribuídas. Elas aparecem quando o envio
                    é concluído com sucesso neste canal (falhas não geram conversa ligada).
                  </Typography>
                ) : (
                  <Stack spacing={1.25} divider={<Divider flexItem />}>
                    {(data.leadsSample || []).map((lead) => (
                      <Stack
                        key={lead.id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1.5}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {lead.clientName || `Conversa #${lead.id}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {lead.originName || lead.originTipo
                              ? `${lead.originName || lead.originTipo} · `
                              : ""}
                            {lead.createdAt
                              ? new Date(lead.createdAt).toLocaleString("pt-BR")
                              : "—"}
                            {lead.status != null
                              ? ` · ${labelLeadStatus(lead.status)}`
                              : ""}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRequestPopup(lead.id);
                          }}
                        >
                          Abrir
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </InfoPanel>
            ) : null}

            <InfoPanel
              icon={<InfoOutlinedIcon fontSize="small" />}
              title="Por que alguns não foram"
              subtitle="O que aconteceu com cada falha, e a solicitação do contato."
            >
              {!failures.length ? (
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                  Nenhuma falha ou bloqueio registrado até agora. Quando houver, os motivos
                  aparecem aqui.
                </Typography>
              ) : (
                <Stack spacing={1.75} divider={<Divider flexItem />}>
                  {failures.map((f) => {
                    const failure = presentFailure(f);
                    const items = failure.items.length
                      ? failure.items
                      : failureContacts.filter((contact) => failureContactMatches(contact, failure));
                    return (
                      <FailureReasonBlock
                        key={failure.reason}
                        failure={{ ...failure, items }}
                        loadingContacts={failureContactsLoading}
                        onOpenLead={(leadId) => openRequestPopup(leadId)}
                      />
                    );
                  })}
                </Stack>
              )}
            </InfoPanel>
          </Box>
        </Stack>
      )}

      <Dialog
        open={Boolean(bucketDialog)}
        onClose={() => setBucketDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
          {bucketDialog?.title || "Solicitações"}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            {formatCount(bucketTotal) || "0"} neste indicador. Clique para abrir a solicitação em uma nova janela.
          </Typography>
          <IconButton
            aria-label="Fechar"
            onClick={() => setBucketDialog(null)}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 0, py: 0, maxHeight: 480 }}>
          {bucketLoading ? (
            <Stack alignItems="center" sx={{ py: 5 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : bucketError ? (
            <Typography variant="body2" color="error" sx={{ px: 3, py: 3 }}>
              {bucketError}
            </Typography>
          ) : !bucketItems.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 3, py: 3, lineHeight: 1.5 }}>
              Nenhuma solicitação neste indicador.
            </Typography>
          ) : (
            <List disablePadding>
              {bucketItems.map((item) => {
                const name =
                  item.clientName ||
                  (item.leadId ? `Solicitação #${item.leadId}` : item.address || "Contato");
                const meta = [
                  item.address && item.address !== name ? item.address : null,
                  item.leadStatus != null ? labelLeadStatus(item.leadStatus) : null,
                  item.errorTitle || null,
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <ListItemButton
                    key={item.recipientId || item.leadId || name}
                    disabled={!item.leadId}
                    onClick={() => openRequestPopup(item.leadId)}
                    sx={{ alignItems: "flex-start", py: 1.25, px: 3 }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {name}
                      </Typography>
                      {meta ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", lineHeight: 1.4 }}
                        >
                          {meta}
                        </Typography>
                      ) : null}
                      {item.errorDetail ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}
                        >
                          {item.errorDetail}
                        </Typography>
                      ) : null}
                      {!item.leadId ? (
                        <Typography variant="caption" color="text.secondary">
                          Ainda sem solicitação para abrir.
                        </Typography>
                      ) : null}
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          )}
          {!bucketLoading && bucketItems.length < bucketTotal ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 3, py: 1.5 }}>
              Mostrando {formatCount(bucketItems.length)} de {formatCount(bucketTotal)}.
            </Typography>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
